import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../App';

/**
 * Google-only admin sign-in.
 *
 * The button hands control to Emergent Auth
 * (`https://auth.emergentagent.com`). Emergent handles the Google
 * consent screen and redirects back to `<origin>/admin/dashboard` with
 * `#session_id=<sid>` in the URL fragment. AuthCallback.js picks that
 * up and exchanges it for a Piczoo JWT (whitelist-enforced server side).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AdminLogin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If already signed in, skip the login screen and go straight to the app.
  useEffect(() => {
    if (!loading && user) {
      const dest = location.state?.from?.pathname || '/admin/dashboard';
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  const handleGoogleSignIn = () => {
    // Derive the redirect URL from the live browser origin — the same code
    // has to work on both preview and production hosts without config.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href =
      'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
  };

  return (
    <div
      className="min-h-screen piczoo-bg text-white flex items-center justify-center p-6 relative overflow-hidden"
      data-testid="admin-login-page"
    >
      {/* Ambient pink glows for brand consistency with the landing page */}
      <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-piczoo-500/[0.08] rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[480px] h-[480px] bg-piczoo-500/[0.06] rounded-full blur-[130px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="brand-card rounded-2xl p-8 sm:p-10 w-full max-w-md relative z-10"
        data-testid="admin-login-card"
      >
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_278e4361-55f4-4949-a1d8-315c320d90c3/artifacts/jdjxf7b7_Piczoo%20Logo%20Pink.png"
            alt="Piczoo"
            className="h-9 mx-auto mb-4"
          />
          <h1 className="font-display font-semibold text-2xl sm:text-3xl mb-2">Admin Sign-in</h1>
          <p className="text-white/60 text-sm">
            Only whitelisted Google accounts can access this panel.
          </p>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center gap-3 bg-white text-noir-900 font-medium rounded-full px-6 py-3.5 hover:bg-white/90 transition-all"
          data-testid="google-signin-btn"
        >
          {/* Google logo (inline SVG — no external asset) */}
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <div className="mt-8 flex items-start gap-3 text-xs text-white/50 border border-white/5 rounded-xl p-4">
          <ShieldCheck className="w-4 h-4 text-piczoo-400 flex-shrink-0 mt-0.5" />
          <span>
            Not a whitelisted admin? Contact the super admin at{' '}
            <span className="text-white/70">click@piczoo.in</span> to be invited.
          </span>
        </div>

        <div className="mt-6 pt-6 border-t border-white/5 text-center">
          <a
            href="/"
            className="text-xs text-white/40 hover:text-piczoo-300 transition-colors tracking-[0.18em] uppercase"
            data-testid="back-to-home-link"
          >
            ← Back to Website
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
